import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Button, Card, FlowLayout, Sheet, Skeleton, StatusChip, StepList } from '@/ds'
import type { Step } from '@/ds'
import { useStay } from '@/app/stay/StayProvider'
import { dayMonth } from '@/lib/format'
import { readQuery, useAppNavigate } from '@/lib/query-params'
import { useAsync } from '@/lib/useAsync'
import { featureEnabled } from '@/config/features'
import { acompanantesService } from '@/services/acompanantes'
import { checkinService } from '@/services/checkin'
import type { Occupant } from '@/services/checkin'
import { SectionError } from '../../Estancia/sections/SectionError'
import { newDraft, useCheckinFlow } from '../CheckinFlow'
import { completePath, progressFor, stepPath } from '../flow'

/** Pide a la app de iOS que abra el registro en el navegador (app actual: CheckinForm, inventario D26). */
function openBrowserOnIos(): boolean {
  if (readQuery().device !== 'ios') return false
  console.log('OPEN_BROWSER')
  return true
}

/** Paso 1 · ¿Quiénes venís? (CheckIn.dc.html step=1). Invitaciones pospuestas contra mock (D8). */
export function Ocupantes() {
  const { t, i18n } = useTranslation()
  const { go } = useAppNavigate()
  const { stay, start, update } = useCheckinFlow()
  const [occupants, reload] = useAsync(`occ:${stay.guestCode}`, (signal) => checkinService.getOccupants(stay, signal))
  const [revoking, setRevoking] = useState<Occupant | null>(null)
  const [busy, setBusy] = useState(false)
  const [notice, setNotice] = useState<string | null>(null)
  const { reload: reloadStay } = useStay()
  const lang = i18n.language
  const showInvitations = featureEnabled('invitados')

  const register = (o: Occupant) => {
    if (openBrowserOnIos()) return
    // Como la app actual, primero se elige cómo rellenar los datos (D33); con forceScan solo hay captura.
    const forceScan = stay.registration.rules.forceScan
    start({ ...newDraft(stay, o.isHolder ? 'holder' : 'companion'), ...(forceScan ? { entry: 'capture' as const } : {}) })
    go(stepPath(forceScan ? '2c' : '1c'))
  }
  const complete = (o: Occupant) => {
    if (openBrowserOnIos()) return
    go(completePath(o.id))
  }
  const resend = async (o: Occupant) => {
    if (!o.invitation) return
    await acompanantesService.resend(stay, o.invitation.id)
    setNotice(t('v2.checkin.ocupantes.reenviado', { name: o.name ?? '' }))
    reload()
  }
  const revoke = async () => {
    if (!revoking) return
    setBusy(true)
    try {
      await acompanantesService.revoke(stay, revoking.invitation?.id ?? revoking.id)
      setRevoking(null)
      reload()
    } finally {
      setBusy(false)
    }
  }

  const list = occupants.kind === 'ready' ? occupants.value : []
  const personNumber = (o: Occupant) => list.indexOf(o) + 1
  const toStep = (o: Occupant): Step => {
    const title = o.isHolder ? (o.name ? t('v2.checkin.ocupantes.tu', { name: o.name }) : t('v2.checkin.ocupantes.tuSinNombre')) : (o.name ?? t('v2.checkin.ocupantes.persona', { n: personNumber(o) }))
    switch (o.state) {
      case 'registered': {
        const subtitle = o.isHolder ? t('v2.checkin.ocupantes.titularRegistrada') : o.registeredAt ? t('v2.checkin.ocupantes.registradaEl', { date: dayMonth(o.registeredAt, lang) }) : t('v2.checkin.ocupantes.registrada')
        // El titular no puede quitarse a sí mismo (bundle 2a). Quitar acceso es pospuesto (mock).
        const canRevoke = showInvitations && !o.isHolder && !o.accessRevoked
        return { id: o.id, title, state: 'done', subtitle, ...(canRevoke ? { action: { label: t('v2.checkin.ocupantes.quitarAcceso'), onClick: () => setRevoking(o), variant: 'link' as const } } : {}) }
      }
      case 'incomplete':
        return {
          id: o.id,
          title,
          state: 'progress',
          subtitle: o.missingFields.some((f) => f !== 'signature') ? t('v2.legacy.helpers.Missing_data') : t('v2.checkin.ocupantes.faltaFirma'),
          action: { label: t('v2.checkin.ocupantes.completar'), onClick: () => complete(o), variant: 'primary' },
        }
      case 'invited':
        return {
          id: o.id,
          title: (
            <>
              {title} <StatusChip tone="neutral" icon="clock">{t('v2.checkin.pospuesto')}</StatusChip>
            </>
          ),
          state: 'progress',
          subtitle: t(`v2.checkin.ocupantes.invitado_${o.invitation?.channel ?? 'whatsapp'}`, { date: dayMonth(o.invitation?.sentAt ?? new Date().toISOString(), lang) }),
          action: { label: t('v2.checkin.ocupantes.reenviar'), onClick: () => void resend(o) },
        }
      default:
        return { id: o.id, title, state: 'pending', subtitle: o.isHolder ? t('v2.checkin.ocupantes.titularPendiente') : t('v2.checkin.ocupantes.pendiente'), action: { label: t('v2.checkin.ocupantes.registrar'), onClick: () => register(o), variant: 'primary' } }
    }
  }

  const continueFlow = () => {
    const holderRow = list.find((o) => o.isHolder)
    if (holderRow && holderRow.state === 'pending') return register(holderRow)
    const incomplete = list.find((o) => o.state === 'incomplete')
    if (incomplete) return complete(incomplete)
    const pending = list.find((o) => o.state === 'pending')
    if (pending) return register(pending)
    // Todo registrado: el titular elige la hora de llegada y termina.
    update({ ...newDraft(stay, 'holder', 'arrival') })
    go(stepPath('6'))
  }

  return (
    <FlowLayout
      progress={{ ...progressFor('1', stay, 'holder', null), backLabel: t('v2.checkin.atras.estancia'), onBack: () => go('/') }}
      title={t('v2.checkin.ocupantes.titulo')}
      intro={t('v2.checkin.ocupantes.intro')}
      primary={occupants.kind === 'ready' ? { label: t('v2.checkin.ocupantes.continuarCon', { count: list.length }), onClick: continueFlow } : undefined}
      secondary={{ label: t('v2.checkin.ocupantes.noSe'), onClick: () => go('/') }}
      footerMessage={notice}
    >
      {occupants.kind === 'loading' && (
        <div aria-busy="true" className="checkin__stack">
          <Skeleton height={64} radius={16} />
          <Skeleton height={64} radius={16} />
          <Skeleton height={64} radius={16} />
        </div>
      )}
      {occupants.kind === 'error' && (
        <SectionError
          code={occupants.code}
          onRetry={() => {
            reload()
            reloadStay()
          }}
        />
      )}
      {occupants.kind === 'ready' && (
        <>
          <Card flush>
            <StepList steps={list.map(toStep)} collapseWhenDone={false} />
          </Card>
          {showInvitations && (
            <>
              <Button variant="secondary" icon="plus" onClick={() => go(stepPath('1b'))}>
                {t('v2.checkin.ocupantes.invitar')}
              </Button>
              <p className="checkin__note">{t('v2.checkin.ocupantes.alcance')}</p>
            </>
          )}
        </>
      )}
      {showInvitations && <Sheet
        open={revoking !== null}
        title={t('v2.checkin.ocupantes.quitarTitulo', { name: revoking?.name ?? '' })}
        confirmLabel={t('v2.checkin.ocupantes.quitarAcceso')}
        destructive
        loading={busy}
        onConfirm={() => void revoke()}
        onCancel={() => setRevoking(null)}
      >
        <p>{t('v2.checkin.ocupantes.quitarTexto')}</p>
      </Sheet>}
    </FlowLayout>
  )
}
